import type { AuthnMethod } from '@imprivata-cloud/authn';
import { jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import moment from 'moment';
import { defaultSystemData, SearchParams } from '@api/constants';
import { _getUrl, getTenantIdFromUrl } from '@api/utils';

export const setToLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

// Used to turn the path into a key for translations and graphics
//
// TODO: Fix this monstrosity
export const getPathKey = (route: string) => {
  if (route.length === 0) {
    return '';
  }

  let updatedRoute = route;

  if (updatedRoute.endsWith('/')) {
    updatedRoute = updatedRoute.slice(0, -1);
  }

  return updatedRoute.slice(1).replaceAll('/', '.');
};

export const getSPMetadataURL = (tenantIdParam?: string) => {
  const tenantId = tenantIdParam
    ? tenantIdParam
    : new URLSearchParams(window.location.search).get(SearchParams.TENANT_ID) || 'no-tenant-id';
  const urls = _getUrl({
    spMetadataUrl: { sub: 'metadata.app', path: `/${tenantId}/saml_metadata/sp_saml_metadata.xml` },
  });
  return urls?.spMetadataUrl;
};

export const getPortalUrl = (tenantId?: string) => {
  const urls = _getUrl({
    portalUrl: { sub: tenantId ? `${tenantId}.access` : 'access', path: '/' },
  });
  return urls?.portalUrl;
};

const isLocalDevRedirectMode =
  import.meta.env.DEV && import.meta.env.VITE_LOCAL_DEV_REDIRECTS === 'true';
const LOCAL_IDP_DEFAULT_BASE = 'https://idp.app.dev.imprivata.cloud:8080';

const logLocalRedirectDebug = (scope: string, details: Record<string, unknown>) => {
  if (!import.meta.env.DEV) { return; }
  console.info(`[am-portal local-redirect] ${scope}`, details);
};

const getIdpLookupBaseOverride = (): string | null => {
  const override = import.meta.env.DEV
    ? (import.meta.env.VITE_IDP_LOOKUP_BASE_URL as string | undefined)
    : undefined;
  return override || null;
};

const getIdpBaseOverride = (tenantId?: string): string | null => {
  const override = import.meta.env.DEV
    ? (import.meta.env.VITE_IDP_BASE_URL as string | undefined)
    : undefined;
  if (!override) { return null; }
  if (override.includes('{tenantId}')) {
    return tenantId ? override.replace('{tenantId}', tenantId) : null;
  }
  return override;
};

const resolveIdpBase = (tenantId?: string, isLookup = false): string | null => {
  if (isLookup) {
    const lookupOverride = getIdpLookupBaseOverride();
    if (lookupOverride) {
      logLocalRedirectDebug('resolveIdpBase.lookupOverride', { tenantId, base: lookupOverride });
      return lookupOverride;
    }
  }

  const idpOverride = getIdpBaseOverride(tenantId);
  if (idpOverride) {
    logLocalRedirectDebug('resolveIdpBase.idpOverride', { tenantId, base: idpOverride });
    return idpOverride;
  }

  if (isLocalDevRedirectMode) {
    logLocalRedirectDebug('resolveIdpBase.localDefault', {
      tenantId,
      base: LOCAL_IDP_DEFAULT_BASE,
    });
    return LOCAL_IDP_DEFAULT_BASE;
  }

  logLocalRedirectDebug('resolveIdpBase.none', { tenantId, isLookup });
  return null;
};

const buildIdpUrl = (path: string, tenantId?: string, isLookup = false): string | null => {
  const baseUrl = resolveIdpBase(tenantId, isLookup);
  if (!baseUrl) { return null; }
  try {
    return new URL(path, baseUrl).toString();
  } catch {
    return null;
  }
};

const buildRedirectUrlWithTarget = (
  baseUrl: string,
  targetUrl: string,
  workflowId?: string,
): string => {
  try {
    const url = new URL(baseUrl);
    if (workflowId) {
      url.searchParams.set('workflowId', workflowId);
    }
    url.searchParams.set('target', targetUrl);
    return url.toString();
  } catch {
    const params = new URLSearchParams();
    if (workflowId) {
      params.set('workflowId', workflowId);
    }
    params.set('target', targetUrl);
    const joiner = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${joiner}${params.toString()}`;
  }
};

// Sets the return path on a base URL; local-dev also carries the port and tenant
// (production uses a per-tenant subdomain instead).
const buildTargetUrl = (
  baseUrl: string,
  path: string,
  { localPort = false, tenantId }: { localPort?: boolean; tenantId?: string } = {},
): string => {
  try {
    const url = new URL(baseUrl);
    url.pathname = path;
    if (localPort && window.location.port) {
      url.port = window.location.port;
    }
    if (tenantId) {
      url.searchParams.set(SearchParams.TENANT_ID, tenantId);
    }
    return url.toString();
  } catch {
    return baseUrl;
  }
};

// Return target follows the app section: MII (`/me`) returns to MII, everything
// else to the portal home. Same host, only the path differs.
const getLocalAwarePortalTarget = (tenantId?: string): string => {
  const targetPath = isMii() ? '/me' : '/';

  if (!isLocalDevRedirectMode) {
    const portalUrl = getPortalUrl(tenantId);
    // On localhost there is no derived host, so fall back to the current origin.
    return buildTargetUrl(portalUrl ?? window.location.origin, targetPath);
  }

  const basePortalUrl = getPortalUrl();
  if (!basePortalUrl) {
    return buildTargetUrl(window.location.origin, targetPath, { tenantId });
  }
  return buildTargetUrl(basePortalUrl, targetPath, { localPort: true, tenantId });
};

export function isSetup(): boolean {
  return !!window?.location?.pathname?.startsWith('/setup');
}

// MII is mounted at `/me`; matched precisely so `/menu` doesn't count.
export function isMii(): boolean {
  const path = window?.location?.pathname ?? '';
  return path === '/me' || path.startsWith('/me/');
}

export const getUrlParam = (param: string): string | null => {
  return new URLSearchParams(window.location.search).get(param);
};

// tenant-lookup resolves the tenant, so its target must drop any tenant
// subdomain (`<tenantId>.access...`) to match a direct portal logout. (PORT-476)
const getTenantAgnosticHref = (): string => {
  try {
    const url = new URL(window.location.href);
    const tenantInfo = getTenantIdFromUrl(url.hostname);
    if (tenantInfo) {
      url.hostname = tenantInfo.urlWithoutTenantId;
    }
    return url.toString();
  } catch {
    return window.location.href;
  }
};

export const getIDPUrl = (tenantId: string, workflowId: string, isTestTenant = false) => {
  const urls = _getUrl({ idpUrl: { sub: `${tenantId}.sys`, path: '/idp-web/idp/authenticate' } });
  const idpUrl = buildIdpUrl('/idp-web/idp/authenticate', tenantId) || urls?.idpUrl;

  let targetUrl = getUrlParam('target') || window.location.href;
  if (isTestTenant) {
    targetUrl = getLocalAwarePortalTarget(tenantId);
  }

  return buildRedirectUrlWithTarget(idpUrl, targetUrl, workflowId);
};

export const getSSOLogoutUrl = (tenantId: string, workflowId: string) => {
  const urls = _getUrl({ ssoUrl: { sub: `${tenantId}.sys`, path: '/idp-web/idp/start-slo' } });
  const idpUrl = buildIdpUrl('/idp-web/idp/start-slo', tenantId) || urls?.ssoUrl;
  const targetUrl = getUrlParam('target') || getLocalAwarePortalTarget();
  return buildRedirectUrlWithTarget(idpUrl, targetUrl, workflowId);
};

export const getIdpLookupUrl = (
  isTestTenant = false,
  tenantId?: string,
  targetOverride?: string,
) => {
  const urls = _getUrl({ idpUrl: { sub: 'idp.app', path: '/tenant-lookup' } });
  const idpUrl = buildIdpUrl('/tenant-lookup', tenantId, true) || urls?.idpUrl;
  let targetUrl = getUrlParam('target') || targetOverride || getTenantAgnosticHref();
  if (isTestTenant) {
    targetUrl = getLocalAwarePortalTarget(tenantId);
  }
  const redirectUrl = buildRedirectUrlWithTarget(idpUrl, targetUrl);
  logLocalRedirectDebug('getIdpLookupUrl', {
    tenantId,
    isTestTenant,
    isLocalDevRedirectMode,
    envLookupBase: import.meta.env.VITE_IDP_LOOKUP_BASE_URL,
    envIdpBase: import.meta.env.VITE_IDP_BASE_URL,
    derivedIdpUrl: urls?.idpUrl,
    selectedIdpUrl: idpUrl,
    targetUrl,
    redirectUrl,
  });
  return redirectUrl;
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      console.debug('Copied to clipboard');
    })
    .catch(e => {
      console.error(`Copy to clipboard FAILED!: ${text}. E: `, e);
    });
};

// from 1970-01-01T00:00:00Z
// to   1970-01-01 00:03:00 UTC+03 or w/o +03 for UTC
export function formatDateInUtcWithTz(date?: string) {
  if (!date) {
    return null;
  }

  return moment(date)
    .format('YYYY-MM-DD HH:mm:ss [UTC]ZZ')
    .slice(0, -2) // Remove minutes from the TZ. Like +0300 -> +03
    .replace('+00', ''); // If UTC TZ, remove the +00 hours shift altogether
}

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export function toIsoDateString(date?: Date) {
  // Do not use toISOString() - returns in UTC messes your TZ, use moment() - returns in local TZ.
  return date && moment(date).format(ISO_DATE_FORMAT);
}

// Reports whose request takes `format: date-time` bounds applied in UTC need whole-day
// boundaries. The picker hands back local Dates, so keep the calendar day the user actually
// clicked and pin it to that same date in UTC instead of letting the browser offset shift it
// into the neighbouring day.
//
// A range ending today additionally has to reach the present: once UTC has rolled into
// tomorrow, today's UTC day-end is already behind "now" (at UTC-5, during the local day's last
// five hours), and events that just happened would be dropped. An earlier end date keeps its own
// day-end — extending that one would report days the user did not select.
//
// The two bounds are returned together because they are only valid as a pair: reaching forward
// can push the window past the span the API accepts, so it pulls the start along with it.
export function utcRangeBounds(from: Date, to: Date, maxDays: number) {
  const dayStart = Date.parse(`${moment(from).format(ISO_DATE_FORMAT)}T00:00:00.000Z`);
  const dayEnd = Date.parse(`${moment(to).format(ISO_DATE_FORMAT)}T23:59:59.999Z`);
  const end = moment(to).isSame(moment(), 'day') ? Math.max(dayEnd, Date.now()) : dayEnd;
  const start = Math.max(dayStart, end - maxDays * MS_PER_DAY + 1);

  return {
    startTimestamp: new Date(start).toISOString(),
    endTimestamp: new Date(end).toISOString(),
  };
}

export const Regex = {
  phone: /^\+?(?:[0-9 ().-]*[0-9]){7}[0-9 ().-]*$/,
  samlDomain: /^[^\s]+\.[^\s]+$/,
  imprIdPDomain: /^[0-9a-zA-Z-]+$/,
  // Starts or ends with -
  imprIdPDomainHyphens: /^[^-].+[^-]$/,
  // Has - in the 3rd or 4th position
  imprIdPDomainHyphensPosition: /^..[^-][^-]/,
  // Block &=_, etc.; cannot start or end with .
  imprIdPUsername: /^[^.][^&=_'"\-+,<>@\s]+[^.]$/i,
  // Block more than one period (.) in a row
  imprIdPUsernameTwoDots: /^(?!.*\.\.).*$/,
  emailLocalPart: /^[^\s@]+$/,
};

export const prepareAuthnData = (authnMethod: AuthnMethod, factorData: string) => {
  return {
    factorType: authnMethod,
    factorData,
    deviceData: '',
    agentData: '',
    systemData: jsonToBase64(defaultSystemData),
  };
};

export async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export const downloadAsFileToUserComputer = (data: Blob, fileName: string): void => {
  const url = globalThis.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  globalThis.URL.revokeObjectURL(url);
};

export const downloadFromUrl = (url: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = '';
  // Signed installer URLs are cross-origin, so browsers ignore `download` and
  // would navigate the current tab. target=_blank keeps the portal SPA loaded
  // (falling back to a throwaway tab), which also makes `rel` meaningful.
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.click();
};

export const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function lastXDaysDateRange(daysAgo: number): { from: Date; to: Date } {
  if (daysAgo < 1) {
    throw new Error('daysAgo must be at least 1');
  }
  const to = new Date();
  return { from: new Date(to.getFullYear(), to.getMonth(), to.getDate() - (daysAgo - 1)), to };
}

export function isObject(value: unknown): value is Record<PropertyKey, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isKeyOfObject<T extends object>(obj: T, key: unknown): key is keyof T {
  return (
    (typeof key === 'string' || typeof key === 'number' || typeof key === 'symbol') &&
    Object.hasOwn(obj, key)
  );
}
